import { useLayoutEffect, useRef, useState } from "react";
import {
  equirectHorizonLineRect,
  PANORAMA_HORIZON_NORM_Y,
  type EquirectHorizonLineRect,
} from "../../lib/ptzPanoramaMap.ts";

export type PanoramaHorizonLineProps = {
  variant?: "main" | "pip";
  normY?: number;
};

function rectsEqual(a: EquirectHorizonLineRect, b: EquirectHorizonLineRect): boolean {
  return (
    a.visible === b.visible &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5
  );
}

export function PanoramaHorizonLine({
  variant = "main",
  normY = PANORAMA_HORIZON_NORM_Y,
}: PanoramaHorizonLineProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<EquirectHorizonLineRect>({
    left: 0,
    top: 0,
    width: 0,
    visible: false,
  });
  const [rect, setRect] = useState<EquirectHorizonLineRect>(rectRef.current);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const commit = () => {
      const r = el.getBoundingClientRect();
      const next = equirectHorizonLineRect(r.width, r.height, normY);
      if (rectsEqual(rectRef.current, next)) return;
      rectRef.current = next;
      setRect(next);
    };

    commit();
    const ro = new ResizeObserver(commit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [normY]);

  const hostClass =
    variant === "pip"
      ? "ds-preview-horizon-line-host ds-preview-horizon-line-host--pip"
      : "ds-preview-horizon-line-host";

  return (
    <div ref={hostRef} className={hostClass} aria-hidden>
      {rect.visible ? (
        <div
          className="ds-preview-horizon-line"
          style={{ left: rect.left, top: rect.top, width: rect.width }}
        />
      ) : null}
    </div>
  );
}
